/* ==========================================================================
   Ozeret -- application shell
   Phone frame, app bar, tab bar, the active screen, sheets and toast.
   ========================================================================== */

import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Icon } from './components/Icon';
import { InstallPrompt } from './components/InstallPrompt';
import { Tour, seenTour } from './components/Tour';
import { Avatar, Wordmark } from './components/ui';
import { Sheets } from './screens/sheets';
import { SignIn } from './screens/SignIn';
import { SharedLink } from './screens/shared/Shared';
import { SCREENS, TITLES } from './screens/registry';
import { NOTIFICATIONS, THREADS } from './data/seed';
import { t } from './lib/i18n';
import { autoOn, listenForNotificationTaps, registerWorker } from './lib/push';
import { PushAsk } from './components/PushCard';
import { byId, cleaner, house } from './lib/domain';
import {
  HOUSE_IDS, activeHouse, back, boot, closeSheet, commit, listenForBack, listenForUpdates, nav, openFromNotification, openSheet, signOut,
  setWantsSubscribe, startEditCleaner, startEditHome, startSubscription, state, tab,
  useStore, wantsSubscribe,
} from './store';
import type { ScreenName } from './store';

interface TabDef { v: ScreenName; label: string; icon: string }

const TABS: Record<'house' | 'cleaner', TabDef[]> = {
  house: [
    { v: 'h_find', label: 'tab_find', icon: 'search' },
    { v: 'h_requests', label: 'tab_requests', icon: 'brief' },
    { v: 'h_post', label: 'tab_post', icon: 'plus' },
    { v: 'h_messages', label: 'tab_chats', icon: 'chat' },
    { v: 'h_profile', label: 'tab_myhome', icon: 'home' },
  ],
  cleaner: [
    { v: 'c_jobs', label: 'tab_work', icon: 'search' },
    { v: 'c_calendar', label: 'tab_calendar', icon: 'cal' },
    { v: 'c_bookings', label: 'tab_myjobs', icon: 'brief' },
    { v: 'c_messages', label: 'tab_chats', icon: 'chat' },
    { v: 'c_profile', label: 'tab_profile', icon: 'user' },
  ],
};

function chatTitle(): string {
  const th = byId(THREADS, state.params.id ?? '');
  if (!th) return 'Chat';
  return state.role === 'cleaner'
    ? house(th.houseId)?.displayName ?? 'Chat'
    : cleaner(th.cleanerId)?.name ?? 'Chat';
}

export default function App() {
  useStore();

  useEffect(() => {
    void boot(); listenForBack(); listenForUpdates();
    registerWorker();
    listenForNotificationTaps((path) => void openFromNotification(path));
  }, []);

  /* Whoever is signed in on this device gets its notifications, if the
     browser already allows them here. */
  const signedIn = state.me && !state.me.isDemo ? state.me.userId : null;
  useEffect(() => {
    if (signedIn) void autoOn(state.lang);
  }, [signedIn]);

  /* Somebody signed in for real who has never been walked round gets the
     walk once. Not in a look-around: the banner is doing enough talking. */
  useEffect(() => {
    if (state.me && !state.me.isDemo && state.tour === null && !seenTour(state.role)) {
      state.tour = state.role;
      /* Without this the flag is set and nothing re-renders, so the walk
         appears at whatever the person happens to tap next -- which is worse
         than not appearing at all. */
      commit();
    }
  }, [state.me?.userId]);

  if (!state.booted) {
    return (
      <div className="stage">
        <div className="phone" style={{ display: 'grid', placeItems: 'center' }}>
          <span className="sub">Loading…</span>
        </div>
      </div>
    );
  }

  /* A shared link comes before sign-in: the whole point is that somebody who
     is not a member can read it. */
  if (state.shareToken) {
    return (
      <div className="stage">
        <div className="stage-inner">
          <div className="phone">
            <div className="statusbar">
              <span>9:41</span>
              <span className="bars">▪▪▪ ᯤ ▮</span>
            </div>
            <div className="screen no-appbar"><SharedLink /></div>
          </div>
        </div>
      </div>
    );
  }

  if (!state.me) {
    return (
      <div className="stage">
        <div className="stage-inner">
          <div className="phone">
            <div className="statusbar">
              <span>9:41</span>
              <span className="bars">▪▪▪ ᯤ ▮</span>
            </div>
            <div className="screen no-appbar"><SignIn /></div>
          </div>
        </div>
      </div>
    );
  }

  return <SignedIn />;
}

/* Which list a screen was opened from. Only these get the two-pane
   treatment: a screen with no list behind it has nothing to sit beside. */
const PARENT_OF: Partial<Record<ScreenName, ScreenName>> = {
  c_job: 'c_jobs',
  c_booking: 'c_bookings',
  h_request: 'h_requests',
  h_booking: 'h_requests',
  h_pastjob: 'h_requests',
  h_cleaner: 'h_find',
  review: 'c_bookings',
};

/** True on a screen with room for two panes. Watched rather than measured
 *  once, so dragging a window narrow puts it back to one. */
function useWide(): boolean {
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1200px)').matches,
  );
  useEffect(() => {
    const q = window.matchMedia('(min-width: 1200px)');
    const on = () => setWide(q.matches);
    q.addEventListener('change', on);
    return () => q.removeEventListener('change', on);
  }, []);

  /* An admin can pin the layout to check the other one without finding
     another device. Everybody else follows their window. */
  if (state.viewport === 'mobile') return false;
  if (state.viewport === 'desktop') return true;
  return wide;
}

function SignedIn() {
  const tabs = TABS[state.role];
  const isTab = tabs.some((x) => x.v === state.view);
  const isChat = state.view === 'c_chat' || state.view === 'h_chat';
  const unread = NOTIFICATIONS[state.role].filter((n) => n.unread).length;
  /* Messages get their own count on the Chats tab. The bell is for everything
     else, and will carry more kinds of alert over time. */
  const unreadChats = THREADS.reduce(
    (n, th) => n + (state.role === 'house' ? th.unreadForHouse : th.unreadForCleaner),
    0,
  );
  const multiProp = state.role === 'house' && HOUSE_IDS.length > 1;

  const Screen = SCREENS[state.view];
  const title = isChat ? chatTitle() : t(TITLES[state.view] || '');

  /* On a real screen, opening something should not hide the list you opened
     it from: past 1200px the list stays on the left and the thing you picked
     opens beside it.

     What it does NOT do any more is hold that second pane open while nothing
     is picked. Reserving 59% of a 27-inch screen for a panel that says "pick
     something" is worse than the problem it was meant to solve -- it is dead
     space with a caption. Nothing picked means the list itself gets the whole
     width, and .cardgrid lays it out in as many columns as the monitor will
     take. The width goes to cleaners you can compare, which is what it is
     for. */
  const wide = useWide();
  /* A review is written from either side's bookings; a home was shown the
     cleaner's "My jobs" behind it, which says cleaner accounts only. */
  const parent = state.view === 'review'
    ? (state.role === 'house' ? 'h_requests' : 'c_bookings')
    : PARENT_OF[state.view];
  const twoPane = wide && !isChat && !!parent;
  const ListScreen = parent ? SCREENS[parent] : null;

  /* One bar across the top naming ONE of the two panes is the phone's chrome
     surviving into a layout it was never for. With a job open it read "The
     job", in a bar 1,484px wide, sitting above a list of OTHER jobs and that
     list's own filters -- the title describing the right-hand pane and
     hovering over the left one.

     So the panes are titled separately: the bar names the section you are in,
     which is the list, and the detail carries its own head. The back arrow
     goes with it. Back is a phone idea -- it means "put the list back",
     and the list never left. What replaces it is a close, which is the thing
     you actually want: the list, full width, nothing selected. */
  const barTitle = twoPane && parent ? t(TITLES[parent] || '') : title;
  const showBack = !isTab && !twoPane;

  const screenRef = useRef<HTMLDivElement>(null);
  const lastView = useRef<string>('');
  useEffect(() => {
    if (lastView.current !== state.view && screenRef.current) {
      screenRef.current.scrollTop = 0;
      lastView.current = state.view;
    }
  });

  return (
    <div className={'stage' + (state.viewport === 'mobile' ? ' vp-mobile' : '')}>
      <div className="stage-inner">
        <div className="phone">
          <div className="statusbar">
            <span>9:41</span>
            <span className="bars">▪▪▪ ᯤ ▮</span>
          </div>

          {/* For accounts that predate the sign-up form asking.

              New accounts cannot get here: the postal code, and the address
              or the areas, are required before the account exists. This is
              the older population -- five households carrying a postal code
              they never chose, and two cleaners with no areas at all.

              The button used to go to the profile, which is the screen that
              DISPLAYS an address rather than the one that takes one. So it
              worked exactly as written and did nothing you could see: you
              arrived at your own home page, still with nowhere to type.

              It opens the form -- through the same starter the "Edit this
              home" button uses, not a bare nav(). The edit screens render
              from state.edit, and navigating straight to one leaves that null
              and crashes it into the error boundary. */}
          {/* One band, not three loose banners.

              Without a wrapper each of these auto-placed into the desktop
              grid on its own and landed in the nav column: 244px wide, at
              y=829, wrapped over three lines. The notice that says you cannot
              post a job yet was in the least-looked-at corner of the window. */}
          <div className="notices">
          {state.profile && !state.profile.ok && (
            <div className="trialbar over">
              <span>{state.profile.why}</span>
              <button
                type="button"
                onClick={() => (state.role === 'cleaner' ? startEditCleaner() : startEditHome())}
              >
                Add it
              </button>
            </div>
          )}

          {/* Where the free month stands.

              Silent for most of it: a countdown from day thirty is nagging,
              not information. It appears in the last week, when the date
              starts to matter, and stays once the month is up -- at which
              point it is the only thing on screen explaining why the buttons
              have stopped working. */}
          {/* ...and for somebody who asked to pay on the way in and did not
              end up at Stripe. On Oster's phone that redirect never landed,
              and without this the app simply put him on a free month he had
              not chosen, silently. wantsSubscribe outlives the redirect, so
              the way to pay is in front of him the moment he arrives. */}
          {state.billing && state.billing.standing === 'trial'
            && (state.billing.daysLeft <= 7 || wantsSubscribe()) && (
            <div className="trialbar">
              <span>
                {wantsSubscribe()
                  ? 'You chose to subscribe. Finish it here.'
                  : state.billing.daysLeft === 0
                    ? 'Your free month ends today.'
                    : state.billing.daysLeft + (state.billing.daysLeft === 1
                      ? ' day left of your free month.' : ' days left of your free month.')}
              </span>
              <button type="button" onClick={() => void startSubscription()}>
                Subscribe — {state.billing.price} per month
              </button>
              {/* A way out.

                  Oster: "I want a dismiss option, like this it just becomes a
                  trial." Right: this bar exists because a redirect to Stripe
                  did not land, which is our problem, not the reader's. Having
                  raised it once, it must take no for an answer -- otherwise a
                  banner apologising for our own bug sits on top of every
                  screen for a month.

                  Dismissing forgets the intent, so the free month carries on
                  exactly as if they had chosen it, and the bar comes back at
                  the usual time: the last week, when the date actually
                  matters. Subscribing is still one tap away in Settings. */}
              {wantsSubscribe() && (
                <button
                  type="button"
                  className="barclose"
                  aria-label="Dismiss and carry on with the free month"
                  onClick={() => { setWantsSubscribe(false); commit(); }}
                >
                  ✕
                </button>
              )}
            </div>
          )}
          {state.billing && state.billing.standing === 'browsing' && (
            <div className="trialbar over">
              <span>Looking around is free. Posting, messaging and applying need a subscription.</span>
              <button type="button" onClick={() => void startSubscription()}>
                Subscribe — {state.billing.price} per month
              </button>
            </div>
          )}
          </div>

          {/* A look-around says so, all the time. Somebody who thinks they
              have booked a cleaner and finds nothing there tomorrow has been
              misled by us, not by the example. */}
          {state.me?.isDemo && (
            <div className="demobar">
              <span>{t('s_demobanner')}</span>
              <button type="button" onClick={() => void signOut()}>{t('s_demoexit')}</button>
            </div>
          )}

          <div className="appbar">
            {showBack && (
              <button type="button" className="back" onClick={back} aria-label="Back">
                <Icon name="back" />
              </button>
            )}
            <h1>{barTitle}</h1>

            {multiProp && isTab && (
              <button
                type="button"
                className="propsw"
                onClick={() => openSheet({ name: 'property' })}
              >
                <Avatar p={activeHouse()} size="sm" />
                <span>{activeHouse().displayName}</span>
                <Icon name="swap" />
              </button>
            )}

            <button
              type="button"
              className="iconbtn"
              onClick={() => openSheet({ name: 'lang' })}
              aria-label="Language"
            >
              <Icon name="globe" />
            </button>
            {!isChat && (
              <button
                type="button"
                className="iconbtn"
                onClick={() => nav('notifications')}
                aria-label="Updates"
              >
                <Icon name="bell" />
                {unread > 0 && <span className="dot" />}
              </button>
            )}
          </div>

          {twoPane && ListScreen ? (
            <div className="twopane">
              <div className="pane pane-list">
                <ErrorBoundary resetKey={'list' + parent}>
                  <ListScreen />
                </ErrorBoundary>
              </div>
              <div className="pane pane-detail" ref={screenRef}>
                <div className="pane-head">
                  <h3>{title}</h3>
                  <button type="button" onClick={back} aria-label="Close">
                    <Icon name="x" />
                  </button>
                </div>
                <ErrorBoundary resetKey={state.view + (state.params.id ?? '')}>
                  {Screen ? <Screen /> : <div className="empty">Not built yet</div>}
                </ErrorBoundary>
              </div>
            </div>
          ) : (
            <div
              className="screen"
              ref={screenRef}
              style={isChat ? { display: 'flex', flexDirection: 'column', padding: 0 } : undefined}
            >
              <ErrorBoundary resetKey={state.view + (state.params.id ?? '')}>
                {Screen ? <Screen /> : <div className="empty">Not built yet</div>}
              </ErrorBoundary>
            </div>
          )}

          <div className="tabbar">
            {/* The name, at the top of the sidebar on a desk. Hidden on a
                phone, where the tab bar is a strip along the bottom and the
                name has no business in it. */}
            <div className="sidebrand"><Wordmark size={22} /></div>
            {tabs.map((x) => {
              const badge = x.icon === 'chat' ? unreadChats : 0;
              return (
                <button
                  key={x.v}
                  type="button"
                  className={'tab' + (state.view === x.v ? ' on' : '')}
                  aria-label={t(x.label) + (badge > 0 ? ', ' + badge + ' unread' : '')}
                  aria-current={state.view === x.v ? 'page' : undefined}
                  onClick={() => tab(x.v)}
                >
                  <Icon name={x.icon} />
                  {badge > 0 && <span className="badge">{badge > 9 ? '9+' : badge}</span>}
                  <span>{t(x.label)}</span>
                </button>
              );
            })}
          </div>

          {state.selDays.length > 0 && state.view === 'c_calendar' && !state.sheet && (
            <div className="selbar">
              <b className="grow">
                {state.selDays.length} day{state.selDays.length > 1 ? 's' : ''} selected
              </b>
              <button
                type="button"
                className="btn quiet"
                onClick={() => { state.selDays = []; closeSheet(); }}
              >
                {t('b_cancel')}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => openSheet({ name: 'dayedit', mode: 'available' })}
              >
                Set availability
              </button>
            </div>
          )}

          {/* Above the tab bar and below everything else: an offer, not an
              interruption. Draws nothing at all unless the browser can
              actually do something with it. */}
          <InstallPrompt />
          {/* Once per device, at the top: notifications on? */}
          <PushAsk />

          {/* The first five minutes, once per side. */}
          {state.tour === state.role && <Tour />}

          <Sheets />
          {state.toast && <div className="toast">{state.toast}</div>}
        </div>
      </div>
    </div>
  );
}
